import React, { useState, useEffect, useMemo, memo } from 'react';
import { Link } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import { usePlantData } from '../../../components/data/PlantData';
import { usePortfolioData } from '../../../components/data/PortfolioData';
import SidebarPageLayout from '../../../components/layout/SidebarPageLayout';
import Marquee from '../../../components/layout/Marquee';
import WeatherForecast, { loadWeatherForecastData } from '../../../components/graphs/WeatherForecast';
import { CurrentIrradiance } from '../../../components/graphs/CurrentIrradiance';
import { findCurrentHourIrradiance } from '../../../util/weatherForecastUtils';
import WeatherRadarChart from '../../../components/graphs/WeatherRadarChart';
import MultiLineChart from '../../../components/graphs/MultiLineChart';
import TimescaleTabs, { FORECAST_TIMESCALE_TABS } from '../../../components/layout/TimescaleTabs';
import { usePortfolioDateFormatter } from '../../../utils/usePortfolioDateFormatter';
import ChartTooltip from '../../../components/layout/ChartTooltip';
import { toSlug } from '../../../util/strings';
import Breadcrumbs from '../../../components/layout/Breadcrumbs';
import { convertTemperature, formatTemperature, temperatureSymbol } from '../../../util/temperature';
import { useTemperatureUnit } from '../../../util/useTemperatureUnit';

function normalizePortfolioKey(value = '') {
  return String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
}

const WEATHER_TIMESCALES = [
  { duration: '24hr', key: '24hr' },
  { duration: '48hr', key: '48hr' },
  { duration: undefined, key: 'week' },
];

const WEATHER_LINE_CHARTS = [
  {
    key: 'poa-irradiance',
    title: 'GHI Solar Irradiance',
    unit: 'W/m²',
    color: '#D07F15',
    domain: Object.freeze([0, 1200]),
    tickValues: Object.freeze([1200, 0]),
    midlines: Object.freeze([{ value: 600, label: '600 W/m²' }]),
    wrapperClassName: 'weather-line-chart weather-poa-irradiance mt-3',
  },
  {
    key: 'ambient-temperature',
    title: 'Ambient Temperature',
    unit: '°F',
    color: '#dc2626',
    domain: Object.freeze([0, 120]),
    tickValues: Object.freeze([120, 0]),
    midlines: Object.freeze([{ value: 60, label: '60 °F' }]),
    wrapperClassName: 'weather-line-chart weather-ambient-temperature mt-3',
  },
  {
    key: 'wind-speed',
    title: 'Wind Speed',
    unit: 'm/s',
    color: '#60a5fa',
    domain: Object.freeze([0, 25]),
    tickValues: Object.freeze([25, 0]),
    midlines: Object.freeze([{ value: 12.5, label: '12.5 m/s' }]),
    wrapperClassName: 'weather-line-chart weather-wind-speed mt-3',
  },
  {
    key: 'precipitation',
    title: 'Precipitation',
    unit: 'mm',
    color: '#1d4ed8',
    domain: Object.freeze([0, 100]),
    tickValues: Object.freeze([100, 0]),
    midlines: Object.freeze([{ value: 50, label: '50 mm' }]),
    wrapperClassName: 'weather-line-chart weather-precipitation mt-3',
  },
];

function buildForecastMetricEndpoint(portfolioName, metricKey, timescaleKey) {
  const params = new URLSearchParams({ timescale: timescaleKey });
  return `/api/weather/portfolio/${portfolioName}/forecast/metric/${metricKey}?${params.toString()}`;
}

const WeatherMetricChart = memo(function WeatherMetricChart({
  chart,
  duration,
  dataEndpoint,
  onTooltip,
  timeZone,
}) {
  return (
    <MultiLineChart
      duration={duration}
      color={chart.color}
      domain={chart.domain}
      tickValues={chart.tickValues}
      midlines={chart.midlines}
      unit={chart.unit}
      tooltipUnit={chart.unit}
      wrapperClassName={chart.wrapperClassName}
      headerLeft={<strong style={{ color: chart.color }}>{chart.title}</strong>}
      headerRight={<strong style={{ color: chart.color }}>{chart.unit}</strong>}
      dataEndpoint={dataEndpoint}
      mapValue={chart.mapValue}
      onTooltip={onTooltip}
      timeZone={timeZone}
    />
  );
});

function Weather({ plantName }) {
  const temperatureUnit = useTemperatureUnit();
  const { plant: plantData } = usePlantData();
  const { data: portfolios } = usePortfolioData();
  const portfolioName = toSlug(plantName) ?? "";
  const dateFormatter = usePortfolioDateFormatter({ portfolioName });
  const lastUpdated = useMemo(() => {
    const normalized = normalizePortfolioKey(portfolioName);
    const match = (portfolios ?? []).find((node) => (
      normalizePortfolioKey(node.portfolio_name) === normalized
      || normalizePortfolioKey(node.plant_name) === normalized
    ));
    return match?.last_updated ?? null;
  }, [portfolios, portfolioName]);
  const [activeTab, setActiveTab] = useState(0);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });
  const [forecastData, setForecastData] = useState({
    dailyForecast: [],
    hourlyForecast: [],
  });
  const [visibleChartCount, setVisibleChartCount] = useState(0);

  const todayForecast = forecastData.dailyForecast[0] ?? {};

  const currentDirectNormalIrradiance = useMemo(
    () => findCurrentHourIrradiance(forecastData.hourlyForecast),
    [forecastData.hourlyForecast],
  );

  const activeTimescale = WEATHER_TIMESCALES[activeTab] ?? WEATHER_TIMESCALES[0];
  const weatherCharts = useMemo(() => WEATHER_LINE_CHARTS.map((chart) => {
    if (chart.key !== 'ambient-temperature') {
      return chart;
    }
    const convert = (value) => convertTemperature(value, 'fahrenheit', temperatureUnit);
    return {
      ...chart,
      unit: temperatureSymbol(temperatureUnit),
      domain: [convert(0), convert(120)],
      tickValues: [convert(120), convert(0)],
      midlines: [{
        value: convert(60),
        label: formatTemperature(60, temperatureUnit, { space: true }),
      }],
      mapValue: (value) => convert(value),
    };
  }), [temperatureUnit]);

  const chartEndpoints = useMemo(
    () => Object.fromEntries(
      weatherCharts.map((chart) => [
        chart.key,
        portfolioName
          ? buildForecastMetricEndpoint(portfolioName, chart.key, activeTimescale.key)
          : '',
      ]),
    ),
    [portfolioName, activeTimescale.key, weatherCharts],
  );

  useEffect(() => {
    document.title = `Vivosol Plant Performance Insights Hub | ${portfolioName} / Monitoring / Weather`;
  }, [portfolioName]);

  useEffect(() => {
    let isMounted = true;

    const loadForecast = async () => {
      if (!portfolioName) {
        return;
      }

      try {
        const forecast = await loadWeatherForecastData(portfolioName);
        if (isMounted) {
          setForecastData(forecast);
        }
      } catch (error) {
        console.error('Failed to load weather forecast:', error);
        if (isMounted) {
          setForecastData({ dailyForecast: [], hourlyForecast: [] });
        }
      }
    };

    loadForecast();

    return () => {
      isMounted = false;
    };
  }, [portfolioName]);

  useEffect(() => {
    setVisibleChartCount(0);
    if (!portfolioName) {
      return undefined;
    }

    let cancelled = false;
    let mounted = 0;

    const mountNext = () => {
      if (cancelled || mounted >= weatherCharts.length) {
        return;
      }
      mounted += 1;
      setVisibleChartCount(mounted);
      if (mounted < weatherCharts.length) {
        requestAnimationFrame(mountNext);
      }
    };

    const starter = requestAnimationFrame(mountNext);

    return () => {
      cancelled = true;
      cancelAnimationFrame(starter);
    };
  }, [portfolioName, activeTimescale.key, weatherCharts.length]);

  return (
    <SidebarPageLayout id="weather">
      <div className="content p-5">
        <ChartTooltip tooltip={tooltip} transition={true} />
        <Marquee />
        <div className="d-flex justify-content-between align-items-start gap-3 mt-3">
          <h1 className="mb-0"><Link to={`${plantData.dashboard}`}><strong>{plantData.plant_name}</strong></Link> : {plantData.config} ({plantData.location})</h1>
          {lastUpdated ? (
            <span className="text-nowrap small pt-1" title="KPI last updated">
              <strong>Last Updated:</strong> {dateFormatter.formatUtc(lastUpdated)}
            </span>
          ) : null}
        </div>
        <Breadcrumbs labels={{ portfolioName: plantData.plant_name }} />
        <div className="d-flex align-content-stretch">
          <div>
            <CurrentIrradiance
              currentHour={
                new Date().getHours() + new Date().getMinutes() / 60
              }
              sunrise={todayForecast.sunrise}
              sunset={todayForecast.sunset}
              directNormalIrradiance={currentDirectNormalIrradiance}
              dateLabel={dateFormatter.formatDate(new Date(), {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
              })}
            />
            <WeatherForecast portfolioName={portfolioName} data={forecastData} />
          </div>
          <div className="align-self-top justify-content-center flex-grow-1">
            <WeatherRadarChart
              portfolioName={portfolioName}
              previousDays={365}
              timeZone={dateFormatter.timeZone}
            />
          </div>
        </div>
        <div className="panel charts p-3 mb-5 mt-4">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3 align-items-center">
              <span>7-Day Weather Forecast</span>
            </div>
          </div>
          <div className="body pt-3">
            <TimescaleTabs
              tabs={FORECAST_TIMESCALE_TABS}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
            {weatherCharts.slice(0, visibleChartCount).map((chart) => (
              <WeatherMetricChart
                key={`${chart.key}-${activeTimescale.key}`}
                chart={chart}
                duration={activeTimescale.duration}
                dataEndpoint={chartEndpoints[chart.key]}
                onTooltip={setTooltip}
                timeZone={dateFormatter.timeZone}
              />
            ))}
          </div>
        </div>
      </div>
    </SidebarPageLayout>
  );
}

export default Weather;
